import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { RuleCard } from 'shared/ui/rule-card/rule-card';
import { RuleDrawer } from 'shared/ui/rule-drawer/rule-drawer';
import { getPagePrefix, track } from 'utils/mixpanel';
import { getRuleTrackName } from 'widgets/score-disclosure/helpers';
import { RuleExample, ScoreEnum } from 'store/api/apiTypes';

interface ProductRuleItemProps {
  title: string;
  score: ScoreEnum;
  text: string;
  analysis: RuleExample[];
  color?: 'gray' | 'white';
  showScore?: boolean;
  showAnalysis?: boolean;
  showChip?: boolean;
  restricted?: boolean;
  productNumber?: number;
  typeName?: string;
}

export const ProductRuleItem: React.FC<ProductRuleItemProps> = ({
  title,
  score,
  text,
  analysis,
  color = 'gray',
  showScore = true,
  showAnalysis = true,
  showChip = true,
  restricted = false,
  productNumber,
  typeName,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { isTrial, isTrialExpired } = useCachedSelectedProject();

  const ruleName = getRuleTrackName(title);
  const pagePrefix = getPagePrefix(location.pathname);

  const handleOpen = () => {
    track(`AIS:${pagePrefix}_CRD_ANALYSIS-OPENED`, {
      type: typeName ?? ruleName,
      is_trial: isTrial,
      ...(productNumber && { product: productNumber }),
      ...(typeName && { sub_type: ruleName }),
    });
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const chipContent = (
    <>
      {analysis.length} {t('casesFromWebsite')}
    </>
  );

  return (
    <>
      <RuleCard
        title={title}
        description={restricted ? undefined : text}
        score={score}
        color={color}
        variant='product'
        showScore={showScore}
        showChip={showChip}
        chipContent={chipContent}
        restricted={restricted}
        isTrialExpired={isTrialExpired}
        onOpen={handleOpen}
      />
      <RuleDrawer
        isOpen={isOpen}
        onClose={handleClose}
        title={title}
        description={text}
        score={score}
        showScore={showScore}
        showChip={showChip}
        analysis={analysis}
        showAnalysis={showAnalysis}
        variant='product'
        typeName={typeName}
        productNumber={productNumber}
      />
    </>
  );
};
